import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { AutoGrowTextarea, ErrorBanner, SortTh, lower, rowClassName, sortRows, useGridSort } from './Configuration';
import {
  FilterCount,
  MultiSelect,
  SearchBox,
  anyFilterSet,
  matchesSelection,
} from './registerFilters';
import { useRegisterDirty } from '../unsavedChangesGuard';
import {
  ACCESS_ACTION_BADGE, ACCESS_ACTION_LABELS, ROW_ACTION_BADGE, ROW_ACTION_LABELS,
  downloadImportTemplate, readImportFile, toCommitRow,
} from './projectContactImport';

// Mirrors CONTACT_ORGANIZATIONS in packages/shared -- apps/web can't import it (ADR 0019), and two
// values aren't worth a network round trip to populate a dropdown.
export const CONTACT_ORGANIZATIONS = ['Aumentum', 'Customer'];

// Aumentum blue, customer green: the same two-sides-of-the-table reading the badge palette already
// carries elsewhere.
export const ORG_BADGE = { Aumentum: 'badge-blue', Customer: 'badge-green' };

// Plural key, array value: every filter on these pages is multi-select (Jeremy, 2026-09-03), and an
// empty array means "no filter" rather than "nothing matches".
export const BLANK_CONTACT_FILTERS = { organizations: [], text: '' };

const FILTER_ARRAY_KEYS = ['organizations'];

// Every column that holds words, not just the name -- "who owns Accounting?" is answered by the
// department or the role text as often as by a name.
const SEARCH_FIELDS = ['name', 'office', 'department', 'role', 'email', 'phone'];

export function filterContacts(contacts, filters) {
  const needle = (filters.text || '').trim().toLowerCase();
  return (contacts || []).filter((c) => {
    if (!matchesSelection(filters.organizations, c.organization, c)) return false;
    if (needle && !SEARCH_FIELDS.some((f) => (c[f] || '').toLowerCase().includes(needle))) return false;
    return true;
  });
}

export function isContactFiltered(filters) {
  return anyFilterSet(filters, FILTER_ARRAY_KEYS);
}

export const CONTACT_SORT_VALUE = {
  name: (r) => lower(r.name),
  organization: (r) => lower(r.organization),
  office: (r) => lower(r.office),
  department: (r) => lower(r.department),
  role: (r) => lower(r.role),
  email: (r) => lower(r.email),
  phone: (r) => lower(r.phone),
};

// A hand-added row starts with this and is filled in in place -- same convention BPATracker's
// "New session" placeholder uses, so a fresh row is never invisible or unlabeled in the grid.
const BLANK_CONTACT = { name: 'New contact', organization: 'Aumentum' };

// Every optional field distinguishes "clear it" (explicit null on update) from "leave it alone"
// (omitted) -- see UpdateProjectContactDto. `row` is a live grid row (edited in place), not a
// separate draft -- inline editing writes straight into the same shape this reads.
function buildPayload(row) {
  const textOrClear = (v) => ((v || '').trim() === '' ? null : v.trim());
  return {
    name: row.name.trim(),
    organization: row.organization,
    office: textOrClear(row.office),
    department: textOrClear(row.department),
    role: textOrClear(row.role),
    email: textOrClear(row.email),
    phone: textOrClear(row.phone),
    isStakeholder: !!row.isStakeholder,
  };
}

/**
 * A project's combined contact sheet -- replaces "Project Contact List Template.xlsx", whose title
 * cell reads "Aumentum Technologies / [Customer Name] Project Team Contact Information", so both
 * sides of the team live in one list. Customer-visible in full: it's their own people's details as
 * much as ours.
 *
 * Cells edit in place (click or tab in, type) rather than through a separate Edit form -- Add and
 * Save live on the grid's own toolbar, and a row's own ✕ removes it. Edits are held locally until
 * Save is clicked; navigating away with any pending is guarded (see unsavedChangesGuard.js).
 *
 * One tab of ProjectRegisters.js, which owns the page header and the project picker. Also rendered
 * by CustomerProjectRegisters.js for a customer contact with edit rights on this project (item 5,
 * 2026-09-12) -- `readOnly` collapses the toolbar/import and the grid's write affordances, so the
 * two shells share one implementation instead of a second copy of this state management.
 */
export default function ProjectContactList({ projectId, onCoverageChange, readOnly = false }) {
  const [contacts, setContacts] = useState([]);
  const [filters, setFilters] = useState({ ...BLANK_CONTACT_FILTERS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [sort, toggleSort] = useGridSort('organization');

  useRegisterDirty(dirtyIds.size > 0);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    apiFetch(`/project-contacts?projectId=${projectId}`)
      .then((rows) => {
        setContacts(rows);
        onCoverageChange?.(rows.length > 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterContacts(contacts, filters), [contacts, filters]);
  const sorted = useMemo(
    () => sortRows(filtered, sort, (row, key) => CONTACT_SORT_VALUE[key](row)),
    [filtered, sort],
  );

  function editCell(id, key, value) {
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, [key]: value } : c)));
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  // Add and delete reload the grid from the server, which would discard other rows' unsaved edits,
  // so save them first -- same as Project Budgets' Add Line.
  async function flushDirty(skipId) {
    const ids = [...dirtyIds].filter((id) => id !== skipId);
    if (!ids.length) return;
    await Promise.all(ids.map((id) => apiFetch(`/project-contacts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(buildPayload(contacts.find((r) => r.id === id))),
    })));
    setDirtyIds((prev) => { const next = new Set(prev); ids.forEach((id) => next.delete(id)); return next; });
  }

  async function addContact() {
    setBusy('adding');
    setActionError('');
    try {
      await flushDirty();
      await apiFetch('/project-contacts', {
        method: 'POST',
        body: JSON.stringify({ ...BLANK_CONTACT, projectId }),
      });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function saveAll() {
    setBusy('saving');
    setActionError('');
    try {
      await flushDirty();
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function deleteContact(id) {
    setActionError('');
    try {
      await flushDirty(id);
      await apiFetch(`/project-contacts/${id}`, { method: 'DELETE' });
      setDirtyIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  return (
    <div>
      <ErrorBanner error={error} />
      {actionError && <ErrorBanner error={actionError} />}

      {/* Not gated on `!loading`: the refetch after every save would otherwise unmount the summary
          and filter bar and collapse the page. */}
      {!!contacts.length && (
        <>
          <ContactSummary contacts={contacts} />
          <div className="card" style={{ marginBottom: 16 }}>
            <ContactFilters filters={filters} onChange={setFilters} />
          </div>
        </>
      )}

      {!readOnly && importOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setImportOpen(false)}>
          <div className="modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Import contacts from spreadsheet</span>
              <button className="modal-close" onClick={() => setImportOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <ContactImportPanel projectId={projectId} onImported={load} />
            </div>
          </div>
        </div>
      )}

      <div className="card">
        {!readOnly && (
          <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
            <button className="btn btn-outline" onClick={downloadImportTemplate}>
              Export template
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setImportOpen(true)}
              disabled={dirtyIds.size > 0}
              title={dirtyIds.size > 0 ? 'Save your pending changes first — importing reloads the grid and would discard them' : undefined}
            >
              Import…
            </button>
            <div className="spacer" />
            <button className="btn btn-outline" onClick={addContact} disabled={!!busy}>
              {busy === 'adding' ? 'Adding…' : '+ Add contact'}
            </button>
            <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyIds.size}>
              {busy === 'saving' ? 'Saving…' : dirtyIds.size ? `Save ${dirtyIds.size} change(s)` : 'Saved'}
            </button>
          </div>
        )}
        {loading ? (
          <div style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
        ) : !contacts.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">👥</div>
            <div className="placeholder-title">No contacts yet</div>
            <div className="placeholder-sub">
              {readOnly ? 'No contacts have been logged for this project yet.' : "Add the project team — ours and the customer's — above."}
            </div>
          </div>
        ) : !sorted.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">🔍</div>
            <div className="placeholder-title">No contacts match these filters</div>
            <div className="placeholder-sub">
              {contacts.length} {contacts.length === 1 ? 'contact is' : 'contacts are'} on this
              project — clear the filters to see them.
            </div>
          </div>
        ) : (
          <ContactsGrid
            contacts={sorted}
            total={contacts.length}
            filtered={isContactFiltered(filters)}
            sort={sort}
            onSort={toggleSort}
            dirtyIds={dirtyIds}
            onEdit={editCell}
            onDelete={deleteContact}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Exported: the customer portal's read-only view renders the identical grid with `readOnly`, so the
 * two can't drift on columns or on how an email/phone is presented.
 */
export function ContactsGrid({ contacts, total, filtered, sort, onSort, dirtyIds, onEdit, onDelete, readOnly }) {
  const [confirmId, setConfirmId] = useState('');

  return (
    <>
      {filtered && <FilterCount shown={contacts.length} total={total} noun="contact" />}
      <div className="sheet-wrap">
        <table className="sheet">
          <thead>
            <tr>
              <SortTh label="Name" sortKey="name" sort={sort} onSort={onSort} style={{ minWidth: 160 }} />
              <SortTh label="Organization" sortKey="organization" sort={sort} onSort={onSort} style={{ width: 120 }} />
              <SortTh label="Office" sortKey="office" sort={sort} onSort={onSort} style={{ width: 130 }} />
              <SortTh label="Department / Group" sortKey="department" sort={sort} onSort={onSort} style={{ width: 170 }} />
              <SortTh label="Role / Responsibilities" sortKey="role" sort={sort} onSort={onSort} style={{ minWidth: 220 }} />
              <SortTh label="Email" sortKey="email" sort={sort} onSort={onSort} style={{ width: 210 }} />
              <SortTh label="Phone" sortKey="phone" sort={sort} onSort={onSort} style={{ width: 130 }} />
              <th style={{ width: 46 }} title="Stakeholder">Stakeholder</th>
              <th style={{ width: readOnly ? 12 : 36 }} />
            </tr>
          </thead>
          <tbody>
            {contacts.map((c, i) => (
              <tr key={c.id} className={rowClassName(i, dirtyIds?.has(c.id))}>
                {readOnly ? (
                  <td className="cell-read" style={{ fontWeight: 600 }}>{c.name}</td>
                ) : (
                  <td><input className="cell-input" value={c.name} onChange={(e) => onEdit(c.id, 'name', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-read">
                    <span className={`badge ${ORG_BADGE[c.organization] || 'badge-gray'}`}>{c.organization}</span>
                  </td>
                ) : (
                  <td>
                    <select className="cell-select" value={c.organization} onChange={(e) => onEdit(c.id, 'organization', e.target.value)}>
                      {CONTACT_ORGANIZATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                )}
                {readOnly ? (
                  <td className="cell-read">{c.office || '—'}</td>
                ) : (
                  <td><input className="cell-input" value={c.office || ''} onChange={(e) => onEdit(c.id, 'office', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-read">{c.department || '—'}</td>
                ) : (
                  <td><input className="cell-input" value={c.department || ''} onChange={(e) => onEdit(c.id, 'department', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-read" style={{ whiteSpace: 'pre-wrap' }}>{c.role || '—'}</td>
                ) : (
                  <td><AutoGrowTextarea value={c.role || ''} onChange={(e) => onEdit(c.id, 'role', e.target.value)} /></td>
                )}
                {/* mailto/tel rather than plain text when read-only: the point of a contact list is
                    being one click from contacting someone. */}
                {readOnly ? (
                  <td className="cell-read">{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : '—'}</td>
                ) : (
                  <td><input type="email" className="cell-input" value={c.email || ''} onChange={(e) => onEdit(c.id, 'email', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-read">{c.phone ? <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}>{c.phone}</a> : '—'}</td>
                ) : (
                  <td><input className="cell-input" value={c.phone || ''} onChange={(e) => onEdit(c.id, 'phone', e.target.value)} /></td>
                )}
                <td className="cell-check">
                  {readOnly ? (
                    c.isStakeholder ? '✓' : '—'
                  ) : (
                    <input
                      type="checkbox" checked={!!c.isStakeholder} title="Stakeholder"
                      onChange={(e) => onEdit(c.id, 'isStakeholder', e.target.checked)}
                    />
                  )}
                </td>
                <td className="cell-check">
                  {!readOnly && (
                    confirmId === c.id ? (
                      <button className="icon-btn" onClick={() => { setConfirmId(''); onDelete(c.id); }} title="Confirm delete">✓?</button>
                    ) : (
                      <button className="icon-btn" onClick={() => setConfirmId(c.id)} title="Remove this contact">✕</button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Exported for the customer view, so both sides count the team the same way. */
export function ContactSummary({ contacts }) {
  const byOrg = CONTACT_ORGANIZATIONS.map((org) => ({
    org,
    count: contacts.filter((c) => c.organization === org).length,
  }));
  return (
    <div className="stat-row">
      <div className="stat">
        <div className="stat-value">{contacts.length}</div>
        <div className="stat-label">Total contacts</div>
      </div>
      {byOrg.map(({ org, count }) => (
        <div className="stat" key={org}>
          <div className="stat-value">{count}</div>
          <div className="stat-label">{org}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * The Contact List's bulk import (item 3, 2026-09-12; extended item 2, 2026-09-13) -- same
 * preview/commit shape as Customer Access's spreadsheet import (`externalUserImport.js`/
 * `ExternalUsers.js`'s `ImportPanel`), scoped to this one project instead of resolving a project name
 * per row. Two API calls, no upload: `readImportFile` parses the .xlsx client-side, `preview` sends
 * the plain rows for the API to evaluate against the live contact list, and `commit` re-evaluates and
 * applies them -- nothing here trusts a preview that might be minutes stale by the time it's
 * confirmed.
 *
 * Exported: also mounted on CustomerAccessRequests.js (item 4) -- one import flow, two entry points,
 * since a sheet's "Access to Pulse/SharePoint" columns can seed that log too.
 */
export function ContactImportPanel({ projectId, onImported }) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // lets the same file be picked again after a fix
    if (!file) return;
    setError('');
    setPreview(null);
    setResult(null);
    setFileName(file.name);
    try {
      const parsed = await readImportFile(file);
      if (!parsed.length) {
        setError('No rows found — check the spreadsheet has a header row plus at least one data row.');
        setRows(null);
        return;
      }
      setRows(parsed);
    } catch (err) {
      setError(err.message);
      setRows(null);
    }
  }

  async function runPreview() {
    setPreviewing(true);
    setError('');
    setResult(null);
    try {
      const result = await apiFetch('/project-contacts/import/preview', {
        method: 'POST',
        body: JSON.stringify({ projectId, rows }),
      });
      setPreview(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function runCommit() {
    setCommitting(true);
    setError('');
    try {
      const commitResult = await apiFetch('/project-contacts/import/commit', {
        method: 'POST',
        body: JSON.stringify({ projectId, rows: preview.rows.map(toCommitRow) }),
      });
      setResult(commitResult);
      setPreview(null);
      setRows(null);
      setFileName('');
      onImported();
    } catch (err) {
      setError(err.message);
    } finally {
      setCommitting(false);
    }
  }

  const importableCount = preview ? preview.rows.filter((r) => r.errors.length === 0).length : 0;

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 14 }}>
        Bring in this project's contact list at once. Each row creates a new contact, or updates the
        existing one if its email matches a contact already on this project — unless "Save as
        Contact" is N, which skips the Contact List entirely. "Access to Pulse" and/or "Access to
        SharePoint" log a Customer Access Request for the row regardless; Pulse also adds the person
        to Configuration &gt; Customer Access (an admin still sends the invitation from there).
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" className="btn btn-outline" onClick={downloadImportTemplate}>
          Download template
        </button>
        <label className="btn btn-outline" style={{ cursor: 'pointer', margin: 0 }}>
          Choose file…
          <input type="file" accept=".xlsx" onChange={handleFile} style={{ display: 'none' }} />
        </label>
        {fileName && <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>{fileName}</span>}
      </div>

      {error && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 14 }}>⚠ {error}</div>}

      {result && (
        <div style={{ fontSize: 13, color: 'var(--green)', marginBottom: 14, padding: '10px 12px', background: 'rgba(0,124,100,0.06)', borderRadius: 8 }}>
          Imported: {result.newContacts} new contact{result.newContacts === 1 ? '' : 's'}, {result.updatedContacts} updated
          {result.accessRequestsCreated > 0 && `, ${result.accessRequestsCreated} access request${result.accessRequestsCreated === 1 ? '' : 's'} logged`}.
          {result.skippedRows > 0 && ` ${result.skippedRows} row${result.skippedRows === 1 ? '' : 's'} had a problem — see below.`}
          {result.errors?.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {result.errors.map((e) => (
                <li key={e.rowNumber}>Row {e.rowNumber}: {e.errors.join('; ')}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!preview && rows && (
        <button className="btn btn-primary" onClick={runPreview} disabled={previewing}>
          {previewing ? 'Checking…' : `Preview import (${rows.length} row${rows.length === 1 ? '' : 's'})`}
        </button>
      )}

      {preview && (
        <>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            {preview.summary.totalRows} rows: {preview.summary.newContacts} new, {preview.summary.updatedContacts} updated
            {preview.summary.accessRequests > 0 && `, ${preview.summary.accessRequests} access request${preview.summary.accessRequests === 1 ? '' : 's'}`}
            {preview.summary.errorRows > 0 && (
              <span style={{ color: 'var(--red)' }}> — {preview.summary.errorRows} row{preview.summary.errorRows === 1 ? '' : 's'} with errors</span>
            )}
          </div>
          <div className="sheet-wrap" style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 14 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Row</th>
                  <th style={{ minWidth: 140 }}>Name</th>
                  <th style={{ minWidth: 110 }}>Organization</th>
                  <th style={{ minWidth: 200 }}>Email</th>
                  <th style={{ minWidth: 170 }}>Contact List</th>
                  <th style={{ minWidth: 170 }}>Access</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.rowNumber}>
                    <td className="cell-read">{r.rowNumber}</td>
                    <td className="cell-read">{r.name || '—'}</td>
                    <td className="cell-read">{r.organization || '—'}</td>
                    <td className="cell-read">{r.email || '—'}</td>
                    {r.errors.length ? (
                      <td className="cell-read" colSpan={2}>
                        <span title={r.errors.join('; ')} className="badge badge-yellow">{r.errors[0]}</span>
                      </td>
                    ) : (
                      <>
                        <td className="cell-read">
                          <span className={`badge ${ROW_ACTION_BADGE[r.action]}`}>{ROW_ACTION_LABELS[r.action]}</span>
                        </td>
                        <td className="cell-read">
                          <span className={`badge ${ACCESS_ACTION_BADGE[r.accessAction]}`}>{ACCESS_ACTION_LABELS[r.accessAction]}</span>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-primary" onClick={runCommit}
              disabled={committing || importableCount === 0}
              title={importableCount === 0 ? 'No rows without errors to import' : undefined}
            >
              {committing ? 'Importing…' : `Confirm import (${importableCount} row${importableCount === 1 ? '' : 's'})`}
            </button>
            <button className="btn btn-outline" onClick={() => { setPreview(null); }} disabled={committing}>
              Back
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function ContactFilters({ filters, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <MultiSelect
        label="Organization"
        allLabel="Both sides"
        options={CONTACT_ORGANIZATIONS.map((o) => ({ value: o, label: o }))}
        selected={filters.organizations}
        onChange={(organizations) => onChange({ ...filters, organizations })}
        minWidth={160}
      />
      <SearchBox
        value={filters.text}
        onChange={(text) => onChange({ ...filters, text })}
        placeholder="Name, department, role, email, phone…"
      />
      {isContactFiltered(filters) && (
        <button className="btn btn-outline" onClick={() => onChange({ ...BLANK_CONTACT_FILTERS })}>
          Clear filters
        </button>
      )}
    </div>
  );
}
